import { authenticatedFetch } from "../auth/auth";

const BQ_PROJECT_ID = "martin-test-datalab";

interface BqField {
  name: string;
  type: string;
}

interface BqRow {
  f: { v: string | null }[];
}

interface BqQueryResponse {
  jobComplete: boolean;
  jobReference?: { projectId: string; jobId: string };
  schema?: { fields: BqField[] };
  rows?: BqRow[];
  errors?: { reason: string; message: string }[];
}

export interface BqResultRow {
  [key: string]: string | number | null;
}

const NUMERIC_TYPES = new Set(["INTEGER", "INT64", "FLOAT", "FLOAT64", "NUMERIC", "BIGNUMERIC"]);

export async function runBigQueryQuery(sql: string, billingProject?: string): Promise<BqResultRow[]> {
  const project = billingProject ?? BQ_PROJECT_ID;
  if (!project) throw new Error("No BigQuery project ID configured (VITE_BQ_PROJECT_ID)");

  const url = `https://bigquery.googleapis.com/bigquery/v2/projects/${project}/queries`;
  const response = await authenticatedFetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ query: sql, useLegacySql: false, timeoutMs: 60000, maxResults: 10000 }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`BigQuery query failed (${response.status}): ${errorText}`);
  }

  const data: BqQueryResponse = await response.json();

  if (data.errors?.length) {
    throw new Error(`BigQuery errors: ${data.errors.map(e => e.message).join(", ")}`);
  }

  if (!data.jobComplete) {
    throw new Error("BigQuery query timed out. Try narrowing the date range.");
  }

  if (!data.schema || !data.rows) return [];

  const fields = data.schema.fields;
  return data.rows.map(row =>
    Object.fromEntries(
      row.f.map((cell, i) => {
        const field = fields[i];
        const raw = cell.v;
        if (raw == null) return [field.name, null];
        if (NUMERIC_TYPES.has(field.type)) return [field.name, Number(raw)];
        return [field.name, raw];
      }),
    ),
  );
}
